import isEqual from 'lodash/isEqual'
import {
  setCursorCoord as setCursorCoordState,
  cursor,
  setCursor,
  resetCursor,
  camera,
} from '@/services/map-service'
import Geographic from '@/lib/geographic'

import {
  isWaypointClusteringEnabled,
  WAYPOINT_CLUSTER_LAYER_ID,
  handleClusterClick,
} from '@/lib/waypoint-clustering'
import {
  isLongPressActionActive,
  isMapMarkupBulkSelectEnabled,
} from '@/lib/prototyping-flags'
import { initMapBulkSelect } from '@/lib/map-bulk-select'

// eslint-disable-next-line no-restricted-imports
import { setFeaturesHovered } from '@/lib/map-viewer/viewer-features'
import { getMapFeatures, FEATURE_DETECTION_PADDING } from '@/lib/feature-utils'
import { isDrawing } from '@/lib/mapbox/draw-manager-plugin'

const clickTimeout = 250
let isDoubleClick = false
let isLongPress = false

let longPressTimeout
const longPressThreshold = 1000

/**
 * *(AI-generated)*
 * Registers click, double-click, hover, and conditionally long-press, touch, bulk-select,
 * and cluster event handlers on the map viewer.
 */
export const addFeatureEventHandlers = (
  mapViewer,
  emit,
  useBaseCollisionBox = false
) => {
  const map = mapViewer.map
  // event handlers
  const clickEventHandler = (e) =>
    setTimeout(() => {
      !isDoubleClick &&
        !isLongPress &&
        handleMapClick(mapViewer, e, emit, useBaseCollisionBox)
    }, clickTimeout)
  const dblClickEventHandler = () => handleDblClick()
  const hoverEventHandler = (e) =>
    setFeaturesHovered(map, e, useBaseCollisionBox)
  const mouseDownLongPressEventHandler = (e) =>
    handleMouseDownLongPress(map, e, emit)
  const mouseUpEventHandler = (e) => handleMouseUp(e)
  const mouseDownBulkSelectEventHandler = (e) =>
    handleMouseDownBulkSelect(map, e)

  // add event handlers to listeners
  map.on('click', clickEventHandler)
  map.on('dblclick', dblClickEventHandler)
  map.on('mousemove', hoverEventHandler)

  // Only add mouse down/up event handlers when mobile testbed flag is active
  // Mouse down/up events for desktop, touch start/end events for mobile.  No overlap in event firing
  if (isLongPressActionActive()) {
    map.on('mousedown', mouseDownLongPressEventHandler)
    map.on('mouseup', mouseUpEventHandler)
    map.on('touchstart', mouseDownLongPressEventHandler)
    map.on('touchend', mouseUpEventHandler)
  }

  if (isMapMarkupBulkSelectEnabled.value) {
    map.on('mousedown', mouseDownBulkSelectEventHandler)
  }

  // add cluster hover listeners
  if (isWaypointClusteringEnabled.value) {
    map.on('mouseenter', WAYPOINT_CLUSTER_LAYER_ID, () => {
      setCursor('pointer')
    })
    map.on('mouseleave', WAYPOINT_CLUSTER_LAYER_ID, () => {
      resetCursor()
    })
  }
}

/**
 * *(AI-generated)*
 * Marks a double-click as in progress to suppress the subsequent single-click.
 */
export const handleDblClick = () => {
  isDoubleClick = true

  setTimeout(() => {
    isDoubleClick = false
  }, clickTimeout)
}

/**
 * *(AI-generated)*
 * Handles a single map click by querying rendered features and emitting a map-click event.
 */
export const handleMapClick = (
  mapViewer,
  event,
  emit,
  useBaseCollisionBox = false
) => {
  const map = mapViewer.map
  // ignore clicks during drawing
  if (isDrawing()) {
    return
  }

  // handle any cluster clicks
  if (isWaypointClusteringEnabled.value) {
    const clusterFeatures = map.queryRenderedFeatures(event.point, {
      layers: [WAYPOINT_CLUSTER_LAYER_ID],
    })

    if (clusterFeatures.length) {
      handleClusterClick(map, clusterFeatures)
      return
    }
  }

  // pass on rest of clicks
  const zoom = mapViewer.getZoom().toFixed(2)
  const queryFeatures = getMapFeatures(
    map,
    event.point,
    FEATURE_DETECTION_PADDING,
    useBaseCollisionBox
  )

  // emit queryFeatures
  emit('map-click', queryFeatures, event, zoom)
}

const handleMouseDownLongPress = (map, event, emit) => {
  // ignore mouse-down during drawing
  if (isDrawing()) {
    return
  }

  if (isLongPressActionActive()) {
    const startingMapLocation = camera().location

    longPressTimeout = setTimeout(() => {
      const endingMapLocation = camera().location

      // Only trigger long-press if the mouse has not moved the map while pressing, in order
      // to prevent long-press while dragging the map
      if (isEqual(startingMapLocation, endingMapLocation)) {
        isLongPress = true
        emit('map-long-press', event)
      }
    }, longPressThreshold)
  }
}

const handleMouseDownBulkSelect = (map, event) => {
  if (event.originalEvent.shiftKey) {
    initMapBulkSelect(map, event)
  }
}

const handleMouseUp = () => {
  clearTimeout(longPressTimeout)

  if (isLongPress) {
    setTimeout(() => {
      isLongPress = false
    }, clickTimeout + 10)
  }
}

/**
 * *(AI-generated)*
 * Updates the cursor coordinate state from a map mouse event.
 */
export const setCursorCoord = ({ lngLat }) => {
  const { lat, lng } = lngLat
  const geoPoint = new Geographic(lng, lat)
  setCursorCoordState(geoPoint)
}

/**
 * *(AI-generated)*
 * Adjusts the camera altitude to compensate for a change in terrain exaggeration.
 */
export const adjustCameraHeightForExaggeration = (
  mapViewer,
  newExag,
  oldExag
) => {
  const currentAltitude = mapViewer.getCameraAltitude()

  // get the elevation of the terrain directly underneath the camera
  const groundElev = mapViewer.getCameraGroundElevation() ?? 0

  // calculate how much the elevation of the terrain has changed from exaggeration
  const diffAltitude = groundElev * newExag - groundElev * oldExag

  // add the diffAltitude to the currentAltitude to get the newAltitude to set the camera at
  const newAltitude = currentAltitude + diffAltitude

  // set the new altitude on the camera while maintaining the current camera position
  mapViewer.setCameraAltitude(newAltitude)
}

/**
 * *(AI-generated)*
 * Updates the map cursor to a pointer if any of the provided features are selectable.
 */
export const handleSelectableCursor = (map, features) => {
  // verify cursor is not already being set
  const isCursorDefault = cursor() === 'default'

  if (!isCursorDefault) {
    return
  }

  // verify feature is selectable
  const isSelectable = features.some(
    ({ properties: p }) => p.selectable === true
  )

  if (isSelectable) {
    map.getCanvas().style.cursor = 'pointer'
  } else {
    map.getCanvas().style.cursor = ''
  }
}
